import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { ROUTES } from 'src/constants';
import { Box } from 'src/kit/Box';
import { Flex } from 'src/kit/Flex';
import { LogoTiny } from 'src/components/Logo';
import { getFullUrl, hasSubDomain } from '@src/helpers/url';

const NavLinkWrapper = ({ children, to, exact, as: Component = NavLink, ...props }: any) => {
  if (hasSubDomain()) {
    return (
      <a href={getFullUrl('', to)} {...props}>
        {children}
      </a>
    );
  }
  return (
    <Component to={to} exact={exact} {...props}>
      {children}
    </Component>
  );
};

const StyledNavLink = styled(NavLinkWrapper)`
  color: #8a99a8;
  font-size: 0.875rem;
  text-decoration: none;
  position: relative;
  transition: color 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 1.5px;
    background-color: #2dbd96;
    transition: width 0.3s ease;
  }

  &:hover {
    color: white;
    &::after {
      width: 100%;
    }
  }

  &.active {
    color: white;
    &::after {
      width: 100%;
    }
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownTrigger = styled.button`
  background: none;
  border: none;
  color: #8a99a8;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.3s ease;

  &:hover {
    color: white;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 0;
    height: 1.5px;
    background-color: #2dbd96;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(13, 27, 42, 0.98);
  border: 1px solid rgba(45, 189, 150, 0.2);
  border-radius: 8px;
  padding: 8px 0;
  min-width: 180px;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.2s ease;
  z-index: 1000;
  margin-top: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const DropdownItem = styled(NavLink)`
  display: block;
  padding: 10px 16px;
  color: #8a99a8;
  text-decoration: none;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(45, 189, 150, 0.1);
    color: white;
  }

  &.active {
    color: #2dbd96;
    background: rgba(45, 189, 150, 0.1);
  }
`;

const ArrowDown = styled.span<{ isOpen: boolean }>`
  font-size: 0.7rem;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  z-index: 1001;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    width: 32px;
    height: 32px;
  }
`;

const HamburgerLine = styled.span<{ isOpen: boolean; lineIndex: number }>`
  width: 22px;
  height: 2px;
  background-color: white;
  border-radius: 2px;
  transition: all 0.3s ease;
  transform-origin: center;

  ${({ isOpen, lineIndex }) => {
    if (!isOpen) return '';
    if (lineIndex === 0) return 'transform: translateY(7px) rotate(45deg);';
    if (lineIndex === 1) return 'opacity: 0; transform: scaleX(0);';
    if (lineIndex === 2) return 'transform: translateY(-7px) rotate(-45deg);';
    return '';
  }}
`;

const MobileMenuOverlay = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(13, 27, 42, 0.98);
    backdrop-filter: blur(12px);
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    transition: all 0.3s ease;
    z-index: 998;
  }
`;

const MobileMenuDrawer = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: rgba(13, 27, 42, 0.98);
    backdrop-filter: blur(12px);
    transform: translateY(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    transition: transform 0.3s ease;
    z-index: 999;
    overflow-y: auto;
    padding-top: 64px;
  }
`;

const MobileNavLink = styled(NavLinkWrapper)`
  display: block;
  padding: 18px 24px;
  color: #8a99a8;
  font-size: 1.1rem;
  text-decoration: none;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(45, 189, 150, 0.1);

  &:hover,
  &.active {
    color: white;
    background: rgba(45, 189, 150, 0.1);
  }
`;

const AccordionTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 18px 24px;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(45, 189, 150, 0.1);
  color: #8a99a8;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: white;
    background: rgba(45, 189, 150, 0.1);
  }
`;

const AccordionContent = styled.div<{ isOpen: boolean }>`
  max-height: ${({ isOpen }) => (isOpen ? '200px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: rgba(0, 0, 0, 0.2);
`;

const AccordionItem = styled(NavLink)`
  display: block;
  padding: 16px 24px 16px 40px;
  color: #8a99a8;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover,
  &.active {
    color: #2dbd96;
    background: rgba(45, 189, 150, 0.1);
  }
`;

const MobileCtaContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 32px 24px;
    margin-top: auto;
    border-top: 1px solid rgba(45, 189, 150, 0.15);
  }
`;

const MobileCtaButton = styled.a<{ primary?: boolean }>`
  display: block;
  text-align: center;
  padding: 16px 18px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  background: ${({ primary }) => (primary ? '#2DBD96' : 'transparent')};
  color: ${({ primary }) => (primary ? '#0D1B2A' : 'white')};
  border: ${({ primary }) => (primary ? 'none' : '1px solid #0277bd')};

  &:hover {
    opacity: 0.9;
    transform: scale(1.02);
  }
`;

const MobileLogo = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(45, 189, 150, 0.15);
  }
`;

const NavContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`;

const SkipLink = styled.a`
  position: absolute;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  background: #2dbd96;
  color: #0d1b2a;
  padding: 12px 24px;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: none;
  z-index: 10000;
  transition: top 0.3s ease;

  &:focus {
    top: 16px;
    outline: 2px solid #2dbd96;
    outline-offset: 2px;
  }
`;

const EmployerNav = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      closeButtonRef.current?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
        setAccordionOpen(false);
      }
      if (e.key === 'Tab' && mobileMenuOpen && mobileMenuRef.current) {
        const focusableElements = mobileMenuRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setAccordionOpen(false);
  };

  const handleOverlayClick = () => {
    closeMobileMenu();
  };

  return (
    <NavContainer>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <Flex
        as="nav"
        aria-label="Main navigation"
        style={{
          height: '64px',
          background: 'rgba(13, 27, 42, 0.97)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(45, 189, 150, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1001
        }}
        px={['5%']}
      >
        <NavLinkWrapper to={ROUTES.home} style={{ textDecoration: 'none', color: 'inherit' }}>
          <Flex style={{ alignItems: 'center', gap: '10px' }}>
            <LogoTiny color="#2DBD96" height={28} />
            <Flex
              color="white"
              style={{
                alignItems: 'center',
                gap: '8px',
                fontWeight: 600,
                fontSize: '1.2rem',
                letterSpacing: '-0.02em',
                fontFamily: "'DM Sans', sans-serif"
              }}
            >
              Sainoo
              <Box as="span" style={{ color: '#2DBD96', fontSize: '1.4rem', lineHeight: 0 }}>
                ·
              </Box>
              Calmon Partners
            </Flex>
          </Flex>
        </NavLinkWrapper>

        <Flex
          style={{ gap: '24px', display: 'flex', alignItems: 'center' }}
          css={`
            @media (max-width: 768px) {
              display: none !important;
            }
          `}
        >
          <StyledNavLink to={ROUTES.home} exact>
            Overview
          </StyledNavLink>
          <StyledNavLink to={ROUTES.landingEmployerFeatures}>Features</StyledNavLink>
          <StyledNavLink to={ROUTES.candidates} exact>
            Candidates
          </StyledNavLink>
          <StyledNavLink to={ROUTES.landingEmployerJobs}>Jobs</StyledNavLink>
          <StyledNavLink to={ROUTES.landingEmployerArticles}>Articles</StyledNavLink>

          {/* <DropdownContainer onMouseEnter={() => setDropdownOpen(true)} onMouseLeave={() => setDropdownOpen(false)}>
            <DropdownTrigger type="button">
              Jobs & Resources
              <ArrowDown isOpen={dropdownOpen}>▼</ArrowDown>
            </DropdownTrigger>
            <DropdownMenu isOpen={dropdownOpen}>
              <DropdownItem to={ROUTES.landingEmployerMagazines}>Explore</DropdownItem>
              <DropdownItem to={ROUTES.landingEmployerJobs}>Jobs</DropdownItem>
              <DropdownItem to={ROUTES.landingEmployerResources}>Resources</DropdownItem>
            </DropdownMenu>
          </DropdownContainer> */}

          <Flex style={{ gap: '6px', alignItems: 'center', marginLeft: '12px' }}>
            <a
              href={ROUTES.signUpEmployer}
              style={{
                background: '#2DBD96',
                color: '#0D1B2A',
                padding: '9px 18px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: 600,
                textDecoration: 'none'
              }}
            >
              Book a demo
            </a>
            <a
              href={ROUTES.signIn}
              style={{
                background: '#0277bd',
                color: 'white',
                padding: '9px 18px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: 600,
                textDecoration: 'none'
              }}
            >
              Sign in
            </a>
          </Flex>
        </Flex>

        <HamburgerButton
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          <HamburgerLine isOpen={mobileMenuOpen} lineIndex={0} />
          <HamburgerLine isOpen={mobileMenuOpen} lineIndex={1} />
          <HamburgerLine isOpen={mobileMenuOpen} lineIndex={2} />
        </HamburgerButton>
      </Flex>

      <MobileMenuOverlay isOpen={mobileMenuOpen} onClick={handleOverlayClick} aria-hidden="true" />
      <MobileMenuDrawer
        isOpen={mobileMenuOpen}
        id="mobile-menu"
        ref={mobileMenuRef}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {/* <MobileLogo>
          <LogoTiny color="#2DBD96" height={28} />
          <Flex
            style={{
              alignItems: 'center',
              gap: '8px',
              color: 'white',
              fontWeight: 600,
              fontSize: '1.1rem',
              letterSpacing: '-0.02em',
              fontFamily: "'DM Sans', sans-serif"
            }}
          >
            Sainoo
            <Box as="span" style={{ color: '#2DBD96', fontSize: '1.3rem', lineHeight: 0 }}>
              ·
            </Box>
            Calmon Partners
          </Flex>
        </MobileLogo> */}

        <MobileNavLink to={ROUTES.home} exact onClick={closeMobileMenu}>
          Overview
        </MobileNavLink>
        <MobileNavLink to={ROUTES.landingEmployerFeatures} onClick={closeMobileMenu}>
          Features
        </MobileNavLink>
        <MobileNavLink to={ROUTES.candidates} onClick={closeMobileMenu}>
          Candidates
        </MobileNavLink>
        <MobileNavLink to={ROUTES.landingEmployerJobs} onClick={closeMobileMenu}>
          Jobs
        </MobileNavLink>
        <MobileNavLink to={ROUTES.landingEmployerArticles} onClick={closeMobileMenu}>
          Articles
        </MobileNavLink>

        {/* <AccordionTrigger onClick={() => setAccordionOpen(!accordionOpen)}>
          Jobs & Resources
          <ArrowDown isOpen={accordionOpen}>▼</ArrowDown>
        </AccordionTrigger>
        <AccordionContent isOpen={accordionOpen}>
          <AccordionItem to={ROUTES.landingEmployerMagazines} onClick={closeMobileMenu}>
            Explore
          </AccordionItem>
          <AccordionItem to={ROUTES.landingEmployerJobs} onClick={closeMobileMenu}>
            Jobs
          </AccordionItem>
          <AccordionItem to={ROUTES.landingEmployerResources} onClick={closeMobileMenu}>
            Resources
          </AccordionItem>
        </AccordionContent> */}

        <MobileCtaContainer>
          <MobileCtaButton href={ROUTES.signUpEmployer} primary>
            Book a demo
          </MobileCtaButton>
          <MobileCtaButton href={ROUTES.signIn}>Sign in</MobileCtaButton>
        </MobileCtaContainer>
      </MobileMenuDrawer>
    </NavContainer>
  );
};

export default EmployerNav;
